import React, { useEffect, useState } from "react";
import "../styles/inquiry.css";
import PageHeader from "../../../../components/common/PageHeader";
import { DEFAULT_STAGE, getQuoteMasterList, getStageList, STAGE_CONFIG } from "../../../stage";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import {
    createInquiryActivity,
    deleteInquiryDocument,
    getChannelList,
    getClientSegmentByVertical,
    getEnquiryPaymentTerms,
    getInquiryActivityLogs,
    getInquiryById,
    getInquirySourceList,
    getPLList,
    getProbabilityList,
    getResponsibleUsers,
    getResponsibleUsersByStage,
    getStageListForInquiry,
    replaceInquiryDocument,
    saveWonPaymentTerms,
    updateInquiryDetails,
    uploadInquiryDocuments,
} from "../api/inquiry.api";
import { getClientContacts, getClientList } from "../../../client/api/clientService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { VERTICALS } from "../../../../constants/verticals";
import { getActivePaymentTerms } from "../../../paymentTerms/api/paymentTermsService";
import {
    amountsMatch,
    DECIMAL_AMOUNT_ONINPUT,
    formatAmount,
    formatCurrency,
    formatNumericAmount,
    parsePaymentAmount,
    round2,
    sanitizeDecimalInputValue,
} from "../utils/currency";

/* ================= TYPES ================= */

interface QuoteOption {
    id: number;
    name: string;
    min_amount: string;
    max_amount: string | null;
    is_active: boolean;
}
interface StageOption {
    label: string;
    value: number;
    stage_name: string;
}
interface SelectOption {
    label: string;
    value: number;
}
interface ActivityData {
    assignedTo: SelectOption | null;
    status: any;
    date: string;
    comment: string;
    rfqValue: string;
    hatchwayValue: string;
    quote: any;
}
interface ActivityLog {
    id: number;
    stage_name: string;
    comment: string | null;
    scheduled_activity_date: string | null;
    created_by_name: string;
    activity_assigned_to_name: string;
    value: string | null;
    date_created: string;
    status: string;
}
interface FormDataState {
    client_name: string;
    contact_details: string;
    pl: number | null;
    architect_name: string;
    facade_consultant: string;
    city: string;
    area: string;
    project_name: string;
    state: string;
    pincode: string;
    expected_closing: string;
    assign_to: string;
    channel: string;
    inquiry_source: string;
    responsible_person: string;
    inquiry_received_date: string;
    probability: string;
    special_note: string;
}

interface FormIdsState {
    customer_id: number | null;
    contact_person_id: number | null;
    probability: number | null;
    platform: number | null; // ✅ ADD THIS
    channel: number | null;
    source: number | null;
    inquiry_owner: number | null;
    inquiry_assigned_to: number | null;
}



const InquiryEdit = () => {
    const today = new Date().toISOString().split("T")[0]; // ✅ ADD HERE

    const [showMainCard, setShowMainCard] = useState(true);
    const [stage, setStage] = useState<any>(null);

    const normalizeStage = (stage?: string) =>
        stage?.toLowerCase().replace(/\s|-+/g, "") || "";

    const stageKey = normalizeStage(stage?.label);
    const rules = STAGE_CONFIG[stageKey] || DEFAULT_STAGE;
    useEffect(() => {
        setActivityData(prev => {
            // ✅ Don't override if user already selected date
            if (prev.date) return prev;

            return {
                ...prev,
                date: today,
            };
        });
    }, [stageKey]);
    const navigate = useNavigate();

    const { vertical } = useParams<{ vertical: string }>();

    const verticalConfig = VERTICALS.find(v => v.key === vertical);
    const verticalId = verticalConfig?.id;
    const verticalName = verticalConfig?.label;
    const isLixil = Number(verticalId) === 3;



    // const [stageOptions, setStageOptions] = useState<StageOption[]>([]);

    const [stageOptions, setStageOptions] = useState<StageOption[]>([]);
    const [stagePage, setStagePage] = useState(1);
    const [hasMoreStages, setHasMoreStages] = useState(true);
    const [loadingStages, setLoadingStages] = useState(false);


    const [quoteOptions, setQuoteOptions] = useState<QuoteOption[]>([]);
    const { id } = useParams<{ id: string }>();
    const inquiryId = Number(id);

    const [clientOptions, setClientOptions] = useState<any[]>([]);
    const [selectedClient, setSelectedClient] = useState<any>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const [contactOptions, setContactOptions] = useState<any[]>([]);
    const [selectedContact, setSelectedContact] = useState<any>(null);
    const [segmentOptions, setSegmentOptions] = useState<any[]>([]);
    const [selectedSegment, setSelectedSegment] = useState<any>(null);

    const [probabilityOptions, setProbabilityOptions] = useState<any[]>([]);
    const [plOptions, setPlOptions] = useState<any[]>([]);
    const [channelOptions, setChannelOptions] = useState<any[]>([]);
    const [channelPage, setChannelPage] = useState(1);
    const [hasMoreChannels, setHasMoreChannels] = useState(true);
    const [loadingChannels, setLoadingChannels] = useState(false);

    const [sourceOptions, setSourceOptions] = useState<any[]>([]);
    const [responsibleOptions, setResponsibleOptions] = useState<any[]>([]);
    const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [latestLog, setLatestLog] = useState<ActivityLog | null>(null);
    const [isActivityDirty, setIsActivityDirty] = useState(false);
    const [isStageInitialized, setIsStageInitialized] = useState(false);
    const [isInquiryInitialized, setIsInquiryInitialized] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [newDocuments, setNewDocuments] = useState<File[]>([]);
    const [inquiry, setInquiry] = useState<any>(null);
    const [initialStage, setInitialStage] = useState<string | null>(null);
    const [clientPage, setClientPage] = useState(1);
    const [hasMoreClients, setHasMoreClients] = useState(true);
    const [loadingClients, setLoadingClients] = useState(false);
    const [uploadDocs, setUploadDocs] = useState<
        {
            file: File;
            description: string;
            isEditing: boolean;
        }[]
    >([]);
    const [isUploadingDocs, setIsUploadingDocs] = useState(false);
    const [replaceDocId, setReplaceDocId] = useState<number | null>(null);
    const replaceInputRef = React.useRef<HTMLInputElement | null>(null);
    const [replacingDocId, setReplacingDocId] = useState<number | null>(null);
    const [replacingFileName, setReplacingFileName] = useState<string | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [isWon, setIsWon] = useState(false);
    const [paymentTermsData, setPaymentTermsData] = useState<any>(null);
    const getFileIcon = (fileName: string) => {
        const ext = fileName.split(".").pop()?.toLowerCase();

        switch (ext) {
            case "pdf":
                return "fas fa-file-pdf text-danger";
            case "doc":
            case "docx":
                return "fas fa-file-word text-primary";
            case "xls":
            case "xlsx":
                return "fas fa-file-excel text-success";
            case "jpg":
            case "jpeg":
                return "fas fa-file-image text-warning";
            case "dwg":
                return "fas fa-drafting-compass text-secondary";
            default:
                return "fas fa-file";
        }
    };

    /* ================= FORM DATA (LABELS) ================= */
    useEffect(() => {
        if (!verticalId || !verticalName) {
            toast.error("Invalid vertical");
            navigate("/dashboard", { replace: true });
        }
    }, [verticalId, verticalName, navigate]);

    const [formData, setFormData] = useState<FormDataState>({
        client_name: "",
        contact_details: "",
        pl: null,
        architect_name: "",
        facade_consultant: "",
        city: "",
        area: "",
        project_name: "",
        state: "",
        pincode: "",
        expected_closing: "",
        assign_to: "",
        channel: "",
        inquiry_source: "",
        responsible_person: "",
        inquiry_received_date: "",
        probability: "",
        special_note: "",
    });

    const [formIds, setFormIds] = useState<FormIdsState>({
        customer_id: null,
        contact_person_id: null,
        probability: null,
        platform: null, // ✅
        channel: null,
        source: null,
        inquiry_owner: null,
        inquiry_assigned_to: null,
    });
    /* ================= ACTIVITY ================= */

    const [activityData, setActivityData] = useState<ActivityData>({
        assignedTo: null,
        status: { label: "In Progress", value: "in_progress" },
        date: "",
        comment: "",
        rfqValue: "",
        hatchwayValue: "",
        quote: null,
    });
    const STAGE_DISABLE_RULES: Record<string, string[]> = {
        new: ["rfqvalue", "quotation", "won"],
        rfq: ["quotation", "new", "won"],
        rfqvalue: ["won", "new"],
        quotation: ["new"],
    };
    const [previousStage, setPreviousStage] = useState<any>(null);


    /* ================= FETCH INQUIRY ================= */

    // useEffect(() => {
    //     if (
    //         !isInquiryInitialized &&
    //         inquiryId &&
    //         clientOptions.length &&
    //         plOptions.length &&
    //         channelOptions.length &&
    //         sourceOptions.length &&
    //         probabilityOptions.length &&
    //         stageOptions.length
    //     ) {
    //         fetchInquiryDetails();
    //         setIsInquiryInitialized(true);
    //     }
    // }, [
    //     inquiryId,
    //     clientOptions,
    //     plOptions,
    //     channelOptions,
    //     sourceOptions,
    //     probabilityOptions,
    //     stageOptions,
    //     isInquiryInitialized,
    // ]);

    useEffect(() => {
        if (inquiryId) {
            fetchInquiryDetails();
        }
    }, [inquiryId]);
    const fetchPaymentTerms = async () => {
        try {
            const res = await getEnquiryPaymentTerms(inquiryId);
            setPaymentTermsData(res.data);
        } catch (err) {
            console.error("Failed to fetch payment terms", err);
        }
    };

    /** Seed client/contact from inquiry API so fields are visible before master lists load. */
    const bindClientAndContactFromInquiry = async (d: any) => {
        if (d.customer_id && d.customer_name) {
            const clientOption = {
                label: d.customer_name,
                value: d.customer_id,
            };
            setSelectedClient(clientOption);
            setClientOptions((prev) => {
                const exists = prev.some((c) => c.value === d.customer_id);
                return exists ? prev : [clientOption, ...prev];
            });
        }

        if (d.client_segment != null) {
            setSelectedSegment({
                label: d.client_segment_name || "",
                value: d.client_segment,
            });
        }

        let contacts: SelectOption[] = [];

        if (Array.isArray(d.contact_persons) && d.contact_persons.length) {
            contacts = d.contact_persons.map((c: any) => ({
                label: c.name,
                value: c.id,
            }));
        } else if (d.customer_id) {
            try {
                const contactRes = await getClientContacts(d.customer_id);
                contacts = (contactRes.data.results || []).map((c: any) => ({
                    label: c.name,
                    value: c.id,
                }));
            } catch {
                // Some roles may not have client-contacts API access
            }
        }

        if (d.contact_person_id && d.contact_person_name) {
            const exists = contacts.some((c) => c.value === d.contact_person_id);
            if (!exists) {
                contacts = [
                    { label: d.contact_person_name, value: d.contact_person_id },
                    ...contacts,
                ];
            }
        }

        if (contacts.length) {
            setContactOptions(contacts);

            const matchedContact =
                contacts.find((c) => c.value === d.contact_person_id) ||
                (d.contact_person_id && d.contact_person_name
                    ? { label: d.contact_person_name, value: d.contact_person_id }
                    : null);

            setSelectedContact(matchedContact);
        }
    };

    const fetchInquiryDetails = async () => {
        try {
            const res = await getInquiryById(inquiryId);
            const d = res.data.data;
            setInquiry(d);

            setFormData({
                client_name: d.customer_name || "",
                contact_details: d.contact_person_name || "",
                pl: d.platform || null,
                architect_name: d.architect_name || "",
                facade_consultant: d.facade_consultant || "",
                city: d.city || "",
                area: d.area || "",
                project_name: d.enquiry_title || "",
                state: d.state || "",
                pincode: d.pincode || "",
                expected_closing: d.expected_closing_date
                    ? d.expected_closing_date.slice(0, 7)
                    : "",
                assign_to: d.assigned_to_name || "",
                channel: d.channel_name || "",
                inquiry_source: d.source_name || "",
                responsible_person: d.owner_name || "",
                inquiry_received_date: d.inquiry_received_date
                    ? d.inquiry_received_date.split("T")[0]
                    : "",
                probability: d.probability_name || "",
                special_note: d.remark || "",
            });

            setFormIds({
                customer_id: d.customer_id,
                contact_person_id: d.contact_person_id,
                probability: d.probability,
                platform: d.platform,
                channel: d.channel,
                source: d.source,
                inquiry_owner: d.inquiry_owner,
                inquiry_assigned_to: d.inquiry_assigned_to,
            });

            await bindClientAndContactFromInquiry(d);

            if (responsibleOptions.length) {
                const matchedResponsible =
                    responsibleOptions.find((r) => r.value === d.inquiry_owner) ||
                    responsibleOptions.find((r) => r.label === d.owner_name);

                if (matchedResponsible) {
                    setActivityData((prev) => ({
                        ...prev,
                        assignedTo: matchedResponsible,
                    }));
                }
            }

            const stageName = d.enquiry_stage_name || d.stage_name || "";
            setInitialStage(normalizeStage(stageName));
            setIsWon(stageName.toLowerCase().includes("won"));
        } catch {
            toast.error("Failed to load inquiry details");
        }
    };


    /* ================= FETCH MASTERS ================= */

    useEffect(() => {
        if (!verticalId) return;

        fetchClients(1);
        fetchPL();
        // fetchChannels();
        fetchInquirySources();
        fetchResponsibleUsers();
        fetchStages(1);
        fetchProbability();
        fetchQuotes();
    }, [verticalId]);

    useEffect(() => {
        if (!inquiry) return;
        if (!clientOptions.length || !segmentOptions.length) return;

        const client = clientOptions.find(
            (c: any) => c.value === inquiry.customer_id
        );

        if (client) {
            setSelectedClient(client);
        }

        setSelectedSegment({
            label: inquiry.client_segment_name,
            value: inquiry.client_segment,
        });

        if (client?.value) {
            getClientContacts(client.value)
                .then((res) => {
                    const contacts: SelectOption[] = (res.data.results || []).map((c: any) => ({
                        label: c.name,
                        value: c.id,
                    }));

                    setContactOptions(contacts);

                    const matchedContact =
                        contacts.find((c) => c.value === inquiry.contact_person_id) ||
                        (inquiry.contact_person_id && inquiry.contact_person_name
                            ? {
                                label: inquiry.contact_person_name,
                                value: inquiry.contact_person_id,
                            }
                            : null);

                    if (matchedContact) {
                        setSelectedContact(matchedContact);
                    }
                })
                .catch(() => {
                    // Keep values seeded from bindClientAndContactFromInquiry
                });
        }
    }, [inquiry, clientOptions, segmentOptions]);
    // const fetchClients = async (page: number) => {
    //     const res = await getClientList(page, {}); // ✅ pass empty params

    //     setClientOptions(
    //         res.data.results.map((c: any) => ({
    //             label: c.name,
    //             value: c.id,
    //             segmentName: c.client_segment_name,
    //         }))
    //     );
    // };
    // useEffect(() => {
    //     if (!inquiry || !clientOptions.length) return;

    //     const client = clientOptions.find(
    //         (c: any) => c.value === inquiry.customer_id
    //     );

    //     setSelectedClient(client || null);

    //     if (client?.value) {
    //         getClientContacts(client.value).then(res => {
    //             const contacts: SelectOption[] = res.data.results.map((c: any) => ({
    //                 label: c.name,
    //                 value: c.id,
    //             }));

    //             setContactOptions(contacts);

    //             const matchedContact = contacts.find(
    //                 c => c.value === inquiry.contact_person_id
    //             );

    //             setSelectedContact(matchedContact || null);
    //         });
    //     }
    // }, [clientOptions, inquiry]);


    const fetchClients = async (page = 1) => {
        if (loadingClients) return;
        if (page !== 1 && !hasMoreClients) return;

        try {
            setLoadingClients(true);

            const res = await getClientList(page, {});

            const results = res.data.results || [];

            const mapped = results.map((c: any) => ({
                label: c.name,
                value: c.id,
                segmentName: c.client_segment_name,
            }));

            setClientOptions((prev) => {
                const preserved =
                    page === 1
                        ? prev.filter((p) => !mapped.some((m: { value: number }) => m.value === p.value))
                        : [];

                return page === 1
                    ? [...preserved, ...mapped]
                    : Array.from(
                        new Map([...prev, ...mapped].map((i) => [i.value, i])).values()
                    );
            });

            setHasMoreClients(Boolean(res.data.next));
            setClientPage(page);

        } catch (err) {
            console.error("Failed to fetch clients", err);
            toast.error("Failed to load clients");
        } finally {
            setLoadingClients(false);
        }
    };


    const fetchPL = async () => {
        if (!verticalId) return;
        const res = await getPLList(verticalId);

        setPlOptions(
            res.data.results.map((pl: any) => ({
                label: `${pl.name} / ${pl.code}`,
                value: pl.id,
            }))
        );
    };


    const fetchProbability = async () => {
        const res = await getProbabilityList();
        setProbabilityOptions(
            res.data.results
                .filter((p: any) => p.is_active)
                .map((p: any) => ({
                    label: p.name,
                    value: p.id,
                }))
        );
    };
    useEffect(() => {
        fetchChannels(1);
    }, []);
    const fetchChannels = async (page = 1) => {
        if (loadingChannels) return;
        if (page !== 1 && !hasMoreChannels) return;

        try {
            setLoadingChannels(true);

            const res = await getChannelList({ page });
            const results = res.data.results || [];

            const mapped = results.map((c: any) => ({
                label: c.name,
                value: c.id,
            }));

            setChannelOptions(prev =>
                page === 1
                    ? mapped
                    : Array.from(
                        new Map([...prev, ...mapped].map(i => [i.value, i])).values()
                    )
            );

            setHasMoreChannels(Boolean(res.data.next));
            setChannelPage(page);
        } catch (err) {
            console.error("Failed to fetch channels", err);
            toast.error("Failed to load channels");
        } finally {
            setLoadingChannels(false);
        }
    };


    const fetchInquirySources = async () => {
        const res = await getInquirySourceList();
        setSourceOptions(
            res.data.results.map((s: any) => ({
                label: s.name,
                value: s.id,
            }))
        );
    };
    useEffect(() => {
        if (!verticalId) return;

        getClientSegmentByVertical(verticalId)
            .then((res) => {
                setSegmentOptions(
                    res.data.results.map((s: any) => ({
                        value: s.id,
                        label: s.name, // no need to show vertical name now
                    }))
                );
            })
            .catch(() => {
                toast.error("Failed to load client segments");
            });
    }, [verticalId]);

    const fetchResponsibleUsers = async () => {
        if (!verticalId) return;

        const res = await getResponsibleUsers({
            vertical_id: verticalId,
            exclude_role: "Pre Sales",      // ✅ if required
        });

        setResponsibleOptions(
            res.data.data.map((u: any) => ({
                label: u.name,
                value: u.id,
            }))
        );
    };
    const fetchResponsibleUsersByStageId = async (stageId: number) => {
        if (!verticalId) return;
        const res = await getResponsibleUsersByStage(verticalId, stageId);

        const users: SelectOption[] = res.data.data.map((u: any) => ({
            label: u.name,
            value: u.id,
        }));

        setResponsibleOptions(users);

        // preserve assigned user if still valid
        setActivityData(prev => {
            if (!prev.assignedTo) return prev;

            const matched = users.find(
                u => u.value === prev.assignedTo?.value
            );

            return {
                ...prev,
                assignedTo: matched || null,
            };
        });
    };

    useEffect(() => {
        if (!stage?.value) return;

        // RFQ, RFQ Value, Quotation → stage-based users , "rfqvalue", "quotation"
        if (["rfq", "rfqvalue", "quotation"].includes(stageKey)) {
            fetchResponsibleUsersByStageId(stage.value);
        } else {
            fetchResponsibleUsers();
        }
    }, [stage?.value, stageKey]);



    useEffect(() => {
        if (!inquiry || !responsibleOptions.length) return;

        setFormIds(prev => ({
            ...prev,
            inquiry_owner: inquiry.inquiry_owner,
            inquiry_assigned_to: inquiry.inquiry_assigned_to,
        }));
    }, [responsibleOptions, inquiry]);

    const fetchStages = async (page = 1) => {
        if (loadingStages) return;
        if (page !== 1 && !hasMoreStages) return;

        try {
            setLoadingStages(true);

            const res = await getStageListForInquiry({ page });
            const results = res.data.results || [];

            const mapped = results
                .filter((s: any) => s.is_active)
                .map((s: any) => ({
                    label: s.stage_name,
                    value: s.id,
                    stage_name: s.stage_name,
                }));

            setStageOptions(prev =>
                page === 1 ? mapped : [...prev, ...mapped]
            );

            setHasMoreStages(Boolean(res.data.next));
            setStagePage(page);
        } catch (err) {
            console.error("Failed to fetch stages", err);
            toast.error("Failed to load stages");
        } finally {
            setLoadingStages(false);
        }
    };

    useEffect(() => {
        if (!inquiry || !stageOptions.length) return;

        const matchedStage = stageOptions.find(
            s => s.value === inquiry.enquiry_stage
        );

        if (matchedStage) {
            setStage(matchedStage);
        }
    }, [stageOptions, inquiry]);



    const fetchActivityLogs = async () => {
        try {
            setLoadingLogs(true);
            const res = await getInquiryActivityLogs(inquiryId);
            const logs = res.data.data || [];

            setActivityLogs(logs);

            const last = getLatestLog(logs);
            setLatestLog(last);
        } catch (err) {
            console.error("Failed to fetch activity logs", err);
        } finally {
            setLoadingLogs(false);
        }
    };



    useEffect(() => {
        if (inquiryId) fetchActivityLogs();
    }, [inquiryId]);
    const getLatestLog = (logs: ActivityLog[]) => {
        if (!logs.length) return null;

        return [...logs].sort(
            (a, b) =>
                new Date(b.date_created).getTime() -
                new Date(a.date_created).getTime()
        )[0];
    };




    const populateActivityFromLog = (log: ActivityLog) => {
        const matchedStage = stageOptions.find(
            s => s.stage_name === log.stage_name
        );

        if (matchedStage) {
            setStage(matchedStage);
        }

        setActivityData({
            assignedTo:
                responsibleOptions.find(
                    r => r.label === log.activity_assigned_to_name
                ) || null,
            status: {
                label: formatStatus(log.status),
                value: log.status,
            },
            date: log.scheduled_activity_date || "",
            comment: log.comment || "",
            rfqValue: log.value || "",
            hatchwayValue: log.value || "",
            quote: null,
        });
    };



    const formatDate = (date?: string | null) =>
        date ? new Date(date).toLocaleDateString() : "-";

    const formatStatus = (status: string) =>
        status === "in_progress" ? "In Progress" : "Completed";

    // const today = new Date().toISOString().split("T")[0];

    const fetchQuotes = async () => {
        const res = await getQuoteMasterList();
        setQuoteOptions(res.data.results.filter((q: QuoteOption) => q.is_active));
    };

    /* ================= UPDATE PAYLOAD ================= */

    const buildUpdateInquiryPayload = () => ({
        company: 2,
        vertical: verticalId,
        customer_id: formIds.customer_id,
        contact_person_id: formIds.contact_person_id,
        client_segment: selectedSegment?.value,
        platform: formIds.platform, // ✅ THIS WAS MISSING
        inquiry_owner: formIds.inquiry_owner,
        inquiry_assigned_to: formIds.inquiry_assigned_to,
        ...(!isLixil && {
            enquiry_title: formData.project_name,
            facade_consultant: formData.facade_consultant,
        }),
        probability: formIds.probability,
        architect_name: formData.architect_name,
        channel: formIds.channel,
        source: formIds.source || "",
        state: formData.state,
        city: formData.city,
        area: formData.area,
        pincode: formData.pincode,
        expected_closing_date: formData.expected_closing
            ? `${formData.expected_closing}-01`
            : null,
        inquiry_received_date: formData.inquiry_received_date || null,
        remark: formData.special_note,
    });

    /* ================= ACTIVITY PAYLOAD ================= */

    const buildActivityPayload = (force = false) => {
        const fd = new FormData();

        fd.append("enquiry", String(inquiryId));
        fd.append("stage", String(stage.value)); // ✅ REQUIRED
        if (force) {
            fd.append("force", "true");   // 👈 IMPORTANT
        }


        if (activityData.assignedTo)
            fd.append(
                "activity_assigned_to",
                String(activityData.assignedTo.value)
            );
        if (activityData.status) {   // ✅ ADD THIS
            fd.append("status", activityData.status.value);
        }

        if (activityData.comment) fd.append("comment", activityData.comment);
        if (activityData.date)
            fd.append("scheduled_activity_date", activityData.date);

        // RFQ Value stage
        if (rules.rfqValue && activityData.rfqValue) {
            fd.append("value", activityData.rfqValue);
        }

        // Quotation stage (Hatchway Value)
        if (rules.hatchwayValue && activityData.hatchwayValue) {
            fd.append("value", activityData.hatchwayValue);
        }

        if (activityData.quote) {
            fd.append("quote_master", String(activityData.quote.id));
        }

        return fd;
    };

    const buildOptimisticLog = (): ActivityLog => ({
        id: Date.now(), // temp id
        stage_name: stage?.label || "-",
        comment: activityData.comment || "-",
        scheduled_activity_date: activityData.date || null,
        created_by_name: "You",
        activity_assigned_to_name: activityData.assignedTo?.label || "-",
        value:
            rules.rfqValue
                ? activityData.rfqValue
                : rules.hatchwayValue
                    ? activityData.hatchwayValue
                    : null,
        date_created: new Date().toISOString(),
        status: activityData.status?.value || "in_progress",
    });

    /* ================= SAVE ================= */
    const handleUploadDocChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const newFiles = Array.from(files).map(file => ({
            file,
            description: "",
            isEditing: true,
        }));

        setUploadDocs(prev => [...prev, ...newFiles]);
    };


    const handleReplaceClick = (docId: number, fileName: string) => {
        setReplaceDocId(docId);
        setReplacingDocId(docId);
        setReplacingFileName(fileName); // ✅ store name
        replaceInputRef.current?.click();
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragActive(true);
    };

    const handleDragLeave = () => {
        setIsDragActive(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        if (isWon) return;

        e.preventDefault();
        setIsDragActive(false);

        const files = Array.from(e.dataTransfer.files);
        if (!files.length) return;

        const droppedDocs = files.map(file => ({
            file,
            description: "",
            isEditing: true,
        }));

        setUploadDocs(prev => [...prev, ...droppedDocs]);
    };


    const handleReplaceFileChange = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0];
        if (!file || !replaceDocId) return;

        try {
            await replaceInquiryDocument(replaceDocId, file);
            toast.success("Document replaced successfully");
            await refreshAllData();
        } catch (err: any) {
            const apiMessage =
                err?.response?.data?.message || "Failed to replace document";

            toast.error(apiMessage);
        } finally {
            setReplaceDocId(null);
            setReplacingDocId(null);
            setReplacingFileName(null);
            e.target.value = "";
        }
    };


    const handleDeleteDocument = async (docId: number, fileName?: string) => {
        const result = await Swal.fire({
            title: "Delete document?",
            text: fileName
                ? `This will permanently delete "${fileName}".`
                : "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            focusCancel: true,
        });

        if (!result.isConfirmed) return;

        try {
            await deleteInquiryDocument(docId);
            toast.success("Document deleted");
            await refreshAllData();
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message || "Failed to delete document"
            );
        }
    };

    const openWonPaymentPopup = async (wonAmountValue: string) => {
        try {
            const res = await getActivePaymentTerms();
            const terms = res.data.results;

            const result = await Swal.fire({
                width: 820,
                showCancelButton: true,
                confirmButtonText: "Save",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#1f3b53",
                cancelButtonColor: "#6c757d",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showDenyButton: true,
                denyButtonText: "Skip for Now",

                customClass: {
                    actions: "won-popup-actions",
                    confirmButton: "btn btn-primary",
                    denyButton: "btn btn-outline-primary",
                    cancelButton: "btn btn-secondary"
                },

                buttonsStyling: false,
                focusConfirm: false,
                title: "",
                html: `
<div style="text-align:left;font-family:inherit">

<div style="
display:flex;
justify-content:space-between;
align-items:center;
margin-bottom:16px;
">
<h5 style="margin:0;font-weight:600">
Payment Terms
</h5>

<span style="
background:#e8f5e9;
color:#2e7d32;
padding:4px 10px;
border-radius:6px;
font-size:13px;
font-weight:500
">
Won Stage
</span>
</div>

<div style="
display:flex;
align-items:center;
gap:12px;
margin-bottom:18px
">

<label style="
min-width:110px;
font-weight:500;
color:#444
">
Quotation Amount
</label>

<input
id="wonAmount"
type="number"
step="0.01"
value="${Number(wonAmountValue) || ''}"
readonly
style="
width:160px;
height:36px;
padding:6px 10px;
border:1px solid #ced4da;
border-radius:6px;
background:#f1f3f5;
font-size:14px;
font-weight:600;
text-align:right
"
/>

</div>

<div style="
border:1px solid #e6e6e6;
border-radius:8px;
overflow:hidden
">

<table style="
width:100%;
border-collapse:collapse;
font-size:14px
">

<thead style="
background:#f8f9fa;
border-bottom:1px solid #e6e6e6
">
<tr>
<th style="width:70px;text-align:center;padding:10px">Select</th>
<th style="padding:10px;text-align:left">Payment Term</th>
<th style="width:140px;padding:10px;text-align:right">Amount</th>
</tr>
</thead>

<tbody>

${terms.map((t: any) => `

<tr style="border-top:1px solid #f1f1f1">

<td style="text-align:center;padding:10px">
<input 
type="checkbox"
class="payment-term-checkbox"
data-id="${t.id}"
style="transform:scale(1.2)"
>
</td>

<td style="padding:10px">
<div style="font-weight:500">${t.name}</div>
${t.description ? `<div style="font-size:12px;color:#6c757d">${t.description}</div>` : ""}
</td>

<td style="padding:8px;text-align:right">
<input
  type="text"
  inputmode="decimal"
  class="term-amount"
  data-id="${t.id}" 
  disabled
  oninput="${DECIMAL_AMOUNT_ONINPUT}"
  style="
    width:110px;
    padding:5px 8px;
    border:1px solid #ced4da;
    border-radius:4px;
    text-align:right
  "
/>
</td>

</tr>

`).join("")}

</tbody>

</table>

</div>

<div style="
display:flex;
justify-content:space-between;
align-items:center;
margin-top:14px;
font-size:14px
">



<div id="paymentError" style="font-size:13px;color:#dc3545"></div>
<div>
<span style="font-weight:600">Total:</span> 
₹<span id="paymentTotal">0</span>
</div>
</div>

</div>
`,

                preConfirm: () => {
                    if (Swal.getDenyButton()?.matches(':focus')) {
                        return true;
                    }
                    const amount = round2(Number(wonAmountValue));

                    if (!amount || amount <= 0) {
                        Swal.showValidationMessage("Quotation amount required");
                        return;
                    }

                    const selectedRows = document.querySelectorAll<HTMLInputElement>(".payment-term-checkbox:checked");

                    if (!selectedRows.length) {
                        Swal.showValidationMessage("Select at least one payment term");
                        return;
                    }

                    let total = 0;
                    const payment_terms: { term_id: number; amount: number }[] = [];

                    selectedRows.forEach(cb => {

                        const id = Number(cb.getAttribute("data-id"));

                        const input = document.querySelector(
                            `.term-amount[data-id="${id}"]`
                        ) as HTMLInputElement | null;

                        if (!input) return;

                        const rawValue = input.value.trim();
                        const value = parsePaymentAmount(rawValue);

                        if (!rawValue || isNaN(value) || value <= 0) {
                            Swal.showValidationMessage("Enter valid amount");
                            throw new Error();
                        }

                        total = round2(total + value);

                        payment_terms.push({
                            term_id: id,
                            amount: value
                        });

                    });

                    if (!amountsMatch(total, amount)) {
                        Swal.showValidationMessage(
                            "Sum of payment terms must equal Quotation Amount"
                        );
                        return;
                    }

                    return {
                        won_amount: amount,
                        payment_terms
                    };
                },
                didOpen: () => {

                    const wonAmount = round2(Number(wonAmountValue));

                    const updateTotal = () => {

                        let total = 0;

                        document.querySelectorAll<HTMLInputElement>(".term-amount").forEach(input => {

                            if (!input.disabled) {
                                const val = parsePaymentAmount(input.value);
                                if (!isNaN(val)) total = round2(total + val);
                            }

                        });

                        const totalEl = document.getElementById("paymentTotal");
                        const errorEl = document.getElementById("paymentError");

                        if (totalEl) totalEl.innerText = formatAmount(total);

                        if (errorEl) {
                            if (amountsMatch(total, wonAmount)) {
                                errorEl.innerHTML = "✔ Amount matched";
                                errorEl.style.color = "#198754";
                            } else {
                                errorEl.innerHTML = "⚠ Total must equal Quotation Amount";
                                errorEl.style.color = "#dc3545";
                            }
                        }
                    };

                    const handleTermInput = (input: HTMLInputElement) => {
                        input.value = sanitizeDecimalInputValue(input.value);
                        updateTotal();
                    };

                    document.querySelectorAll(".payment-term-checkbox").forEach((cb: any) => {

                        cb.addEventListener("change", (e: any) => {

                            const target = e.target as HTMLInputElement;
                            const id = target.dataset.id;

                            const input = document.querySelector(
                                `.term-amount[data-id="${id}"]`
                            ) as HTMLInputElement | null;

                            if (!input) return;

                            input.disabled = !target.checked;

                            if (!target.checked) {
                                input.value = "";
                            }

                            updateTotal();
                        });

                    });

                    document.querySelectorAll<HTMLInputElement>(".term-amount").forEach(input => {
                        input.addEventListener("input", () => handleTermInput(input));
                    });

                    updateTotal();

                }
            });

            if (result.isDenied) {
                return {
                    skip: true,
                    won_amount: Number(wonAmountValue)
                };
            }
            const formValues = result.value;

            if (!formValues) return null;
            return {
                skip: false,
                won_amount: formValues.won_amount,
                payment_terms: formValues.payment_terms
            };
        } catch (err) {
            toast.error("Failed to save payment terms");
            return null;
        }
    };
    const handleSaveActivity = async () => {
        let optimisticLog: ActivityLog | null = null;
        const error =
            !stage
                ? "Stage is required"
                : rules.commentRequired && !activityData.comment
                    ? "Comment is required"
                    : null;

        if (error) {
            toast.error(error);
            return;
        }



        try {


            const isWonStage =
                normalizeStage(stage?.label) === "won" &&
                normalizeStage(initialStage || "") !== "won";

            let wonData = null;

            if (isWonStage) {

                const hatchwayAmount =
                    activityData.hatchwayValue ||
                    inquiry?.hatchway_value ||
                    latestLog?.value;

                if (!hatchwayAmount) {
                    toast.error("Hatchway value required before marking Won");
                    return;
                }

                wonData = await openWonPaymentPopup(String(hatchwayAmount));

                if (!wonData) {
                    if (previousStage) {
                        setStage(previousStage);
                    }
                    return;
                }
            }

            /* ⭐ OPTIMISTIC LOG */
            optimisticLog = buildOptimisticLog();

            setActivityLogs(prev => {
                if (!optimisticLog) return prev;
                return [optimisticLog, ...prev];
            });
            // 🔹 First API call
            const response = await createInquiryActivity(
                buildActivityPayload(false)
            );

            // 🟢 CHECK FOR SOFT CONFIRMATION (IMPORTANT FIX)
            if (response?.data?.confirmation_required) {

                // remove optimistic entry
                if (optimisticLog) {
                    const logId = optimisticLog.id;
                    setActivityLogs(prev =>
                        prev.filter(log => log.id !== logId)
                    );
                }
                const result = await Swal.fire({
                    title: "Confirmation Required",
                    text: response.data.message,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Continue",
                    cancelButtonText: "Cancel",
                    confirmButtonColor: "#1f3b53",
                    cancelButtonColor: "#6c757d",
                    reverseButtons: true,
                    width: "500px",
                });

                if (result.isConfirmed) {

                    // add optimistic again
                    if (optimisticLog) {
                        const log = optimisticLog;
                        setActivityLogs(prev => [log, ...prev]);
                    }

                    // 🔥 build same payload
                    const forcePayload = buildActivityPayload(false);

                    // 🔥 just append extra field
                    forcePayload.append("force_save", "true");

                    // 🔥 call SAME API again
                    await createInquiryActivity(forcePayload);


                    await refreshAllData();
                    if (wonData) {

                        const quotationAmount =
                            Number(activityData.hatchwayValue) ||
                            Number(inquiry?.hatchway_value) ||
                            Number(latestLog?.value) ||
                            Number(wonData?.won_amount) ||
                            0;

                        await saveWonPaymentTerms({
                            enquiry_id: inquiryId,
                            quotation_amount: Number(
                                wonData?.won_amount ||
                                activityData.hatchwayValue ||
                                inquiry?.hatchway_value ||
                                latestLog?.value
                            ),
                            skip_payment_terms: wonData?.skip || false,
                            payment_terms: wonData?.payment_terms || []
                        });

                    }
                    setActivityData({
                        assignedTo: null,
                        status: { label: "In Progress", value: "in_progress" },
                        date: today,   // ✅ CHANGE HERE
                        comment: "",
                        rfqValue: "",
                        hatchwayValue: "",
                        quote: null,
                    });

                    toast.success("Inquiry updated & activity added");
                }

                return; // 🚨 STOP normal flow
            }

            // ✅ Normal success
            await refreshAllData();

            if (wonData) {

                const quotationAmount =
                    Number(activityData.hatchwayValue) ||
                    Number(inquiry?.hatchway_value) ||
                    Number(latestLog?.value) ||
                    Number(wonData?.won_amount) ||
                    0;

                await saveWonPaymentTerms({
                    enquiry_id: inquiryId,
                    quotation_amount: Number(
                        wonData?.won_amount ||
                        activityData.hatchwayValue ||
                        inquiry?.hatchway_value ||
                        latestLog?.value
                    ),
                    skip_payment_terms: wonData?.skip || false,
                    payment_terms: wonData?.payment_terms || []
                });

                await fetchPaymentTerms();
            }

            setActivityData({
                assignedTo: null,
                status: { label: "In Progress", value: "in_progress" },
                date: today,   // ✅ CHANGE HERE
                comment: "",
                rfqValue: "",
                hatchwayValue: "",
                quote: null,
            });

            toast.success("Inquiry updated & activity added");

        } catch (err: any) {



            if (optimisticLog) {
                const logId = optimisticLog.id;
                setActivityLogs(prev =>
                    prev.filter(log => log.id !== logId)
                );
            }
            const apiErrors = err?.response?.data;

            if (apiErrors && typeof apiErrors === "object") {
                const mappedErrors: Record<string, string> = {};
                Object.keys(apiErrors).forEach((key) => {
                    mappedErrors[key] = apiErrors[key]?.[0];
                });
                setFieldErrors(mappedErrors);
            }

            toast.error("Failed to save");
        }
    };
    const statusOptions = [
        { label: "In Progress", value: "in_progress" },
        { label: "Completed", value: "completed" },
    ];

    /* ================= JSX (UNCHANGED) ================= */
    const handleClientChange = async (selected: any) => {
        setSelectedClient(selected || null);
        setSelectedContact(null);
        setContactOptions([]);

        setFormData((prev: any) => ({
            ...prev,
            client_name: selected?.label || "",
            contact_details: "",
        }));

        setFormIds((prev: any) => ({
            ...prev,
            customer_id: selected?.value || null,
        }));

        if (!selected?.value) return;


        const contactRes = await getClientContacts(selected.value);
        const contacts = contactRes.data.results.map((c: any) => ({
            label: c.name,
            value: c.id,
            phone: c.mobile_no,
            email: c.email,
        }));

        setContactOptions(contacts);
        if (contacts.length === 1) {
            setSelectedContact(contacts[0]);

            setFormData(prev => ({
                ...prev,
                contact_details: contacts[0].label,
            }));

            setFormIds(prev => ({
                ...prev,
                contact_person_id: contacts[0].value,
            }));
        }
    };


    const isActivityValid = () => {
        if (!stage) return false;

        if (rules.commentRequired && !activityData.comment?.trim()) return false;
        if (rules.date?.required && !activityData.date) return false;
        if (rules.assignedTo && !activityData.assignedTo) return false;
        if (rules.status && !activityData.status) return false;
        if (rules.rfqValue && !activityData.rfqValue) return false;
        if (rules.hatchwayValue?.required && !activityData.hatchwayValue) return false;
        if (rules.quote?.required && !activityData.quote) return false;

        return true;
    };
    const handleInputChange = (field: string) =>
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData((prev: any) => ({
                ...prev,
                [field]: e.target.value,
            }));

    const handleSaveInquiryOnly = async () => {
        try {
            const payload = buildUpdateInquiryPayload();
            const fd = new FormData();

            const ignoreKeys = ["company", "vertical"];

            const normalize = (val: any) => {
                if (!val) return null;

                // normalize date formats
                if (typeof val === "string" && val.includes("T")) {
                    return val.split("T")[0];
                }

                return String(val);
            };

            Object.entries(payload).forEach(([key, value]) => {

                if (ignoreKeys.includes(key)) return;

                let originalValue = inquiry[key];

                // normalize expected_closing_date
                if (key === "expected_closing_date") {
                    originalValue = inquiry.expected_closing_date;
                }

                const newVal = normalize(value);
                const oldVal = normalize(originalValue);

                if (newVal !== oldVal) {
                    fd.append(key, String(value ?? ""));
                }
            });

            let hasChanges = false;
            fd.forEach(() => {
                hasChanges = true;
            });

            if (!hasChanges) {
                toast.info("No changes detected");
                return;
            }

            await updateInquiryDetails(inquiryId, fd);

            toast.success("Inquiry details updated");
            setIsEditMode(false);
            await refreshAllData();

        } catch {
            toast.error("Failed to update inquiry details");
        }
    };
    const handleUploadDocuments = async () => {
        if (uploadDocs.length === 0) {
            toast.error("Please select documents first");
            return;
        }

        try {
            setIsUploadingDocs(true);

            // 🔥 Force finalize all editing rows safely
            const finalizedDocs = uploadDocs.map(d => ({
                file: d.file,
                description: d.description?.trim() || "",
            }));

            await uploadInquiryDocuments(inquiryId, finalizedDocs);

            toast.success("Documents uploaded successfully");

            setUploadDocs([]);
            await refreshAllData();

        } catch (err: any) {
            const apiMessage =
                err?.response?.data?.message || "Failed to upload documents";

            toast.error(apiMessage);
        } finally {
            setIsUploadingDocs(false);
        }
    };



    const handleRemovePendingDoc = async (
        index: number,
        fileName: string
    ) => {
        const result = await Swal.fire({
            title: "Remove file?",
            text: `Are you sure you want to remove "${fileName}" from the upload list?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, remove",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            focusCancel: true,
        });

        if (!result.isConfirmed) return;

        setUploadDocs(prev => prev.filter((_, i) => i !== index));

        Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: "File removed",
            showConfirmButton: false,
            timer: 2000,
        });
    };

    // const isStageOptionDisabled = (option: StageOption) => {
    //     if (!stage?.label) return false;
    //     if (!initialStage) return false; // guard

    //     const currentStageKey = initialStage;
    //     // const currentStageKey = normalizeStage(stage.label);
    //     const targetStageKey = normalizeStage(option.label);
    //     if (targetStageKey === "new" && currentStageKey !== "new") {
    //         return true;
    //     }
    //     const disabledTargets = STAGE_DISABLE_RULES[currentStageKey] || [];

    //     return disabledTargets.includes(targetStageKey);
    // };
    const STAGE_ORDER = ["new", "rfq", "rfqvalue", "quotation", "won"];

    // const isStageOptionDisabled = (option: StageOption) => {
    //     const targetStageKey = normalizeStage(option.label);

    //     // ⭐ ALWAYS DISABLE NEW IN EDIT
    //     if (targetStageKey === "new") {
    //         return true;
    //     }

    //     if (!initialStage) return false;

    //     const currentStageKey = initialStage;

    //     // ⭐ KEEP YOUR FORWARD LOGIC
    //     const disabledTargets = STAGE_DISABLE_RULES[currentStageKey] || [];
    //     if (disabledTargets.includes(targetStageKey)) {
    //         return true;
    //     }

    //     // ⭐ REVERSE BLOCKING
    //     const currentIndex = STAGE_ORDER.indexOf(currentStageKey);
    //     const targetIndex = STAGE_ORDER.indexOf(targetStageKey);

    //     if (targetIndex === -1 || currentIndex === -1) return false;

    //     if (targetIndex < currentIndex) {
    //         return true;
    //     }

    //     return false;
    // };
    const isStageOptionDisabled = (option: StageOption) => {

        const target = normalizeStage(option.label);
        // ✅ Follow Up should never be disabled
        if (target === "followup") {
            return false;
        }
        // ⭐ THIS IS THE FIX
        const savedStage = normalizeStage(
            latestLog?.stage_name || initialStage || ""
        );

        if (!savedStage) return false;

        const STAGE_RULES: Record<string, string[]> = {

            new: ["followup", "showroomvisit", "rfq", "hold", "lost"],

            followup: ["followup", "showroomvisit", "rfq", "hold", "lost"],

            showroomvisit: ["followup", "showroomvisit", "rfq", "hold", "lost"],

            rfq: ["followup", "showroomvisit", "rfq", "rfqvalue", "hold", "lost"],

            rfqvalue: ["followup", "showroomvisit", "rfqvalue", "quotation", "hold", "lost"],

            quotation: ["followup", "showroomvisit", "quotation", "won", "hold", "lost"]

        };

        const allowed = STAGE_RULES[savedStage] || STAGE_RULES["followup"];

        if (target === savedStage) return false;

        if (target === "won" && !allowed.includes("won")) return true;

        if (!Object.keys(STAGE_RULES).includes(target)) return false;

        return !allowed.includes(target);
    };
    const refreshAllData = async () => {
        await fetchInquiryDetails();
        await fetchActivityLogs();
        await fetchPaymentTerms();
    };
    useEffect(() => {
        if (inquiryId) {
            fetchPaymentTerms();
        }
    }, [inquiryId]);
    const handleEditPaymentTerms = async () => {
        try {

            const activeRes = await getActivePaymentTerms();
            const allTerms = activeRes.data.results || [];

            const paymentRes = await getEnquiryPaymentTerms(inquiryId);
            const paymentData = paymentRes.data;

            const savedTerms = paymentData?.payment_terms || [];
            const wonAmount =
                Number(paymentData?.quotation_amount) ||
                Number(inquiry?.hatchway_value) ||
                Number(latestLog?.value) ||
                0;

            const savedIds = savedTerms.map((t: any) => t.term_id);

            const { value: formValues } = await Swal.fire({
                width: 820,
                showCancelButton: true,
                confirmButtonText: "Save",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#1f3b53",
                cancelButtonColor: "#6c757d",
                allowOutsideClick: false,
                allowEscapeKey: false,

                customClass: {
                    actions: "won-popup-actions",
                    confirmButton: "btn btn-primary",
                    cancelButton: "btn btn-secondary"
                },
                title: "",
                html: `
<div style="text-align:left;font-family:inherit">

<div style="
display:flex;
justify-content:space-between;
align-items:center;
margin-bottom:16px;
">
<h5 style="margin:0;font-weight:600">
Edit Quotation Amount & Payment Terms
</h5>

<span style="
background:#e8f5e9;
color:#2e7d32;
padding:4px 10px;
border-radius:6px;
font-size:13px;
font-weight:500
">
Won Stage
</span>
</div>

<div style="
display:flex;
align-items:center;
gap:12px;
margin-bottom:18px
">

<label style="
min-width:110px;
font-weight:500;
color:#444
">
Quotation Amount
</label>

<input
id="wonAmount"
type="number"
step="0.01"
value="${paymentData?.quotation_amount ?? ""}"
readonly
style="
width:160px;
height:36px;
padding:6px 10px;
border:1px solid #ced4da;
border-radius:6px;
background:#f1f3f5;
font-size:14px;
font-weight:600;
text-align:right
"
/>

</div>

<div style="
border:1px solid #e6e6e6;
border-radius:8px;
overflow:hidden
">

<table style="
width:100%;
border-collapse:collapse;
font-size:14px
">

<thead style="
background:#f8f9fa;
border-bottom:1px solid #e6e6e6
">
<tr>
<th style="width:70px;text-align:center;padding:10px">Select</th>
<th style="padding:10px;text-align:left">Payment Term</th>
<th style="width:140px;padding:10px;text-align:right">Amount</th>
</tr>
</thead>

<tbody>

${allTerms.map((t: any) => {

                    const saved = savedTerms.find((s: any) => s.term_id === t.id)

                    return `
<tr style="border-top:1px solid #f1f1f1">

<td style="text-align:center;padding:10px">
<input 
type="checkbox"
class="payment-term-checkbox"
data-id="${t.id}"
${saved ? "checked" : ""}
style="transform:scale(1.2)"
>
</td>

<td style="padding:10px">
<div style="font-weight:500">${t.name}</div>
${t.description ? `<div style="font-size:12px;color:#6c757d">${t.description}</div>` : ""}
</td>

<td style="padding:8px;text-align:right">
<input
  type="text"
  inputmode="decimal"
  class="term-amount"
  data-id="${t.id}"
  value="${saved?.amount ?? ""}"
  placeholder="₹"
  oninput="${DECIMAL_AMOUNT_ONINPUT}"
  style="
    width:110px;
    padding:5px 8px;
    border:1px solid #ced4da;
    border-radius:4px;
    text-align:right
  "
  ${saved ? "" : "disabled"}
/>
</td>

</tr>
`
                }).join("")}

</tbody>

</table>

</div>

<div style="
display:flex;
justify-content:space-between;
align-items:center;
margin-top:14px;
font-size:14px
">

<div>
<span style="font-weight:600">Total:</span> 
₹<span id="paymentTotal">0</span>
</div>

<div id="paymentError" style="font-size:13px;color:#dc3545"></div>

</div>

</div>
`,
                preConfirm: () => {

                    const amount = round2(Number(wonAmount))

                    const selectedRows = document.querySelectorAll<HTMLInputElement>(".payment-term-checkbox:checked")

                    if (!selectedRows.length) {
                        Swal.showValidationMessage("Select at least one payment term")
                        return
                    }

                    let total = 0

                    const payment_terms: any[] = []

                    selectedRows.forEach(cb => {

                        const id = Number(cb.dataset.id)

                        const input = document.querySelector(
                            `.term-amount[data-id="${id}"]`
                        ) as HTMLInputElement

                        const rawValue = input.value.trim();
                        const value = parsePaymentAmount(rawValue);

                        if (!rawValue || isNaN(value) || value <= 0) {
                            Swal.showValidationMessage("Enter valid amount");
                            throw new Error();
                        }

                        total = round2(total + value)

                        payment_terms.push({
                            term_id: id,
                            amount: value
                        })

                    })

                    if (!amountsMatch(total, amount)) {
                        Swal.showValidationMessage("Sum of payment terms must equal Quotation Amount")
                        return
                    }

                    return { payment_terms }

                },
                didOpen: () => {

                    const wonAmount = round2(Number(paymentData?.quotation_amount))

                    const updateTotal = () => {

                        let total = 0

                        document.querySelectorAll<HTMLInputElement>(".term-amount").forEach(input => {

                            if (!input.disabled) {
                                const val = parsePaymentAmount(input.value)
                                if (!isNaN(val)) total = round2(total + val)
                            }

                        })

                        const totalEl = document.getElementById("paymentTotal")
                        const errorEl = document.getElementById("paymentError")

                        if (totalEl) totalEl.innerText = formatAmount(total)

                        if (errorEl) {
                            if (amountsMatch(total, wonAmount)) {
                                errorEl.innerHTML = "✔ Amount matched";
                                errorEl.style.color = "#198754";

                                Swal.resetValidationMessage();

                            } else {
                                errorEl.innerHTML = "⚠ Total must equal Quotation Amount";
                                errorEl.style.color = "#dc3545";
                            }
                        }
                        const confirmBtn = Swal.getConfirmButton();

                        if (confirmBtn) {
                            confirmBtn.disabled = !amountsMatch(total, wonAmount);
                        }

                    }

                    const handleTermInput = (input: HTMLInputElement) => {
                        input.value = sanitizeDecimalInputValue(input.value);
                        Swal.resetValidationMessage();
                        updateTotal();
                    };

                    document.querySelectorAll(".payment-term-checkbox").forEach((cb: any) => {

                        cb.addEventListener("change", (e: any) => {

                            const target = e.target as HTMLInputElement
                            const id = target.dataset.id

                            const input = document.querySelector(
                                `.term-amount[data-id="${id}"]`
                            ) as HTMLInputElement | null

                            if (!input) return

                            input.disabled = !target.checked

                            if (!target.checked) {
                                input.value = ""
                            }

                            updateTotal()

                        })

                    })

                    document.querySelectorAll<HTMLInputElement>(".term-amount").forEach(input => {
                        input.addEventListener("input", () => handleTermInput(input));
                    });

                    updateTotal()

                }
            });

            if (!formValues) return;

            await saveWonPaymentTerms({
                enquiry_id: inquiryId,
                skip_payment_terms: false,
                payment_terms: formValues.payment_terms
            });

            await fetchPaymentTerms();

            toast.success("Payment terms updated");

            await fetchPaymentTerms();

        } catch (err) {
            toast.error("Failed to update payment terms");
        }
    };

    const todayDate = new Date();
    // const today = todayDate.toISOString().split("T")[0];

    const yesterdayDate = new Date();
    yesterdayDate.setDate(todayDate.getDate() - 1);
    const yesterday = yesterdayDate.toISOString().split("T")[0];
    const getCurrentMonth = () => {
        const today = new Date();
        return today.toISOString().slice(0, 7); // yyyy-mm
    };
    return (
        <>
            <PageHeader
                title={`Edit ${verticalName ?? "Inquiry"}`}
                breadcrumbs={[
                    { label: "Sales" },
                    { label: verticalName ?? "Inquiry" },
                    { label: "Edit Inquiry" },
                ]}
            />


            <div className="container-fluid mt-3">

                {/* ================= SINGLE COLLAPSIBLE CARD ================= */}
                <div className={`row ${isWon ? "locked-section" : ""}`}
                    title={isWon ? "This section is locked because the stage is Won" : ""}
                >
                    <div className="col-12">
                        <div className="inquiry-card">

                            {/* HEADER */}
                            <div
                                className="inquiry-card-header cursor-pointer"
                                onClick={() => setShowMainCard(prev => !prev)}
                            >
                                <i className="fas fa-edit"></i>
                                <span>Client, Project & Status Details</span>
                                <i
                                    className={`fas fa-chevron-${showMainCard ? "up" : "down"} ms-auto`}
                                />
                            </div>

                            {/* BODY */}
                            {showMainCard && (
                                <div className="inquiry-card-body">

                                    <div className="row g-4">

                                        {/* ================= CLIENT & PROJECT SECTION ================= */}
                                        <div className="col-lg-8">
                                            <div className="inquiry-section ">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="inquiry-section-title">
                                                        Client Details
                                                    </div>

                                                    {!isEditMode && !isWon && (
                                                        <button
                                                            type="button"
                                                            className="btn  btn-primary"
                                                            onClick={() => setIsEditMode(true)}
                                                        >
                                                            <i className="fas fa-edit me-1"></i>
                                                        </button>
                                                    )}</div>
                                                <div className="row g-3 d-flex align-items-end">
                                                    <div className="col-md-4 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                Client Name <span className="text-danger">*</span>
                                                            </label>
                                                            <div className="form-floating">
                                                                <Select
                                                                    options={clientOptions}
                                                                    value={
                                                                        selectedClient ||
                                                                        clientOptions.find(
                                                                            (opt: any) => opt.label === formData.client_name
                                                                        ) ||
                                                                        null
                                                                    }
                                                                    onChange={handleClientChange}
                                                                    placeholder="Client Name"
                                                                    classNamePrefix="react-select"
                                                                    isDisabled={!isEditMode}
                                                                    isLoading={loadingClients}
                                                                    onMenuScrollToBottom={() => {
                                                                        if (!loadingClients && hasMoreClients) {
                                                                            fetchClients(clientPage + 1);
                                                                        }
                                                                    }}
                                                                />


                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-md-4 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                Contact Details <span className="text-danger">*</span>
                                                            </label>
                                                            <div className="form-floating">
                                                                <Select
                                                                    options={contactOptions}
                                                                    value={selectedContact}

                                                                    onChange={(selected: any) => {
                                                                        setSelectedContact(selected || null);

                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            contact_details: selected?.label || "",
                                                                        }));

                                                                        setFormIds(prev => ({
                                                                            ...prev,
                                                                            contact_person_id: selected?.value || null,
                                                                        }));
                                                                    }}




                                                                    // isDisabled={!selectedClient}
                                                                    isDisabled={!isEditMode}
                                                                    placeholder={
                                                                        selectedClient ? "Select Contact" : "Select Client First"
                                                                    }
                                                                    classNamePrefix="react-select"
                                                                />
                                                            </div>

                                                            {/* <label>Contact Details *</label> */}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                Client Segment <span className="text-danger">*</span>
                                                            </label>
                                                            <Select
                                                                options={segmentOptions}
                                                                value={selectedSegment}
                                                                onChange={(selected) => setSelectedSegment(selected)}
                                                                classNamePrefix="react-select"
                                                                isDisabled={!isEditMode}
                                                                placeholder="Select Client Segment"
                                                            />
                                                        </div>
                                                    </div>

                                                </div>

                                                <div className="inquiry-section-title mt-3">
                                                    Project Details
                                                </div>
                                                <div className="row g-3 d-flex align-items-end">
                                                    {!isLixil && (

                                                        <div className="col-md-12">
                                                            <div className="form-floating">
                                                                <input
                                                                    className="form-control"
                                                                    value={formData.project_name}
                                                                    onChange={(e) =>
                                                                        setFormData((prev: any) => ({
                                                                            ...prev,
                                                                            project_name: e.target.value,
                                                                        }))
                                                                    }
                                                                    disabled={!isEditMode}
                                                                    placeholder="Project Name"
                                                                />
                                                                <label>Project Name</label>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="col-md-6">
                                                        <div className="form-floating">
                                                            <input
                                                                className="form-control"
                                                                value={formData.architect_name}
                                                                onChange={(e) =>
                                                                    setFormData((prev: any) => ({
                                                                        ...prev,
                                                                        architect_name: e.target.value,
                                                                    }))
                                                                }
                                                                disabled={!isEditMode}
                                                            />
                                                            <label>Client Architect Name</label>
                                                        </div>
                                                    </div>
                                                    {!isLixil && (

                                                        <div className="col-md-6">
                                                            <div className="form-floating">
                                                                <input
                                                                    className="form-control"
                                                                    placeholder="Facade Consultant"
                                                                    value={formData.facade_consultant}
                                                                    onChange={(e) =>
                                                                        setFormData((prev: any) => ({
                                                                            ...prev,
                                                                            facade_consultant: e.target.value,
                                                                        }))
                                                                    }
                                                                    disabled={!isEditMode}
                                                                />
                                                                <label>Facade Consultant</label>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="col-md-12">
                                                        <div className="form-floating">
                                                            <input
                                                                type="month"
                                                                className="form-control"
                                                                value={formData.expected_closing}
                                                                onChange={(e) =>
                                                                    setFormData((prev: any) => ({
                                                                        ...prev,
                                                                        expected_closing: e.target.value,
                                                                    }))
                                                                }
                                                                min={getCurrentMonth()}                 // ✅ block past months
                                                                onKeyDown={(e) => e.preventDefault()}   // ✅ block manual typing
                                                                placeholder="Expected Closing"
                                                                disabled={!isEditMode}
                                                            />
                                                            <label>Expected Closing Forecast Month</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-floating">
                                                            <input
                                                                className="form-control"
                                                                value={formData.state}
                                                                onChange={(e) =>
                                                                    setFormData((prev: any) => ({
                                                                        ...prev,
                                                                        state: e.target.value,
                                                                    }))
                                                                }
                                                                disabled={!isEditMode}
                                                                placeholder="State"
                                                            />
                                                            <label>State</label>
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6">
                                                        <div className="form-floating">
                                                            <input
                                                                className="form-control"
                                                                value={formData.city}
                                                                onChange={(e) =>
                                                                    setFormData((prev: any) => ({
                                                                        ...prev,
                                                                        city: e.target.value,
                                                                    }))
                                                                }
                                                                disabled={!isEditMode}
                                                            />

                                                            <label>City *</label>
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6">
                                                        <div className="form-floating">
                                                            <input
                                                                className="form-control"
                                                                value={formData.area}
                                                                onChange={(e) =>
                                                                    setFormData((prev: any) => ({
                                                                        ...prev,
                                                                        area: e.target.value,
                                                                    }))
                                                                }
                                                                disabled={!isEditMode}
                                                            />

                                                            <label>Area</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-floating">
                                                            <input
                                                                className="form-control"
                                                                value={formData.pincode}
                                                                onChange={(e) =>
                                                                    setFormData((prev: any) => ({
                                                                        ...prev,
                                                                        pincode: e.target.value.replace(/\D/g, ""),
                                                                    }))
                                                                }
                                                                disabled={!isEditMode}
                                                                placeholder="Pincode"
                                                            />
                                                            <label>Pincode</label>
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                PL *
                                                            </label>
                                                            <div className="form-floating">
                                                                <Select
                                                                    options={plOptions}
                                                                    value={
                                                                        plOptions.find(
                                                                            (opt: any) => opt.value === formData.pl
                                                                        ) || null
                                                                    }
                                                                    onChange={(selected: any) => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            pl: selected?.value || null,
                                                                        }));

                                                                        setFormIds(prev => ({
                                                                            ...prev,
                                                                            platform: selected?.value || null,
                                                                        }));
                                                                    }}

                                                                    placeholder="PL"
                                                                    classNamePrefix="react-select"
                                                                    isDisabled={!isEditMode}
                                                                />
                                                                {/* <label>PL *</label> */}
                                                            </div>            </div>
                                                    </div>

                                                    <div className="col-md-6 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                Channel
                                                            </label>
                                                            <div className="form-floating">
                                                                <Select
                                                                    options={channelOptions}
                                                                    value={
                                                                        channelOptions.find(opt => opt.value === formIds.channel) || null
                                                                    }
                                                                    onChange={(selected: any) => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            channel: selected?.label || "",
                                                                        }));

                                                                        setFormIds(prev => ({
                                                                            ...prev,
                                                                            channel: selected?.value || null,
                                                                        }));
                                                                    }}
                                                                    placeholder="Channel"
                                                                    classNamePrefix="react-select"
                                                                    isDisabled={!isEditMode}
                                                                    isLoading={loadingChannels}
                                                                    onMenuScrollToBottom={() => {
                                                                        if (!loadingChannels && hasMoreChannels) {
                                                                            fetchChannels(channelPage + 1);
                                                                        }
                                                                    }}
                                                                />



                                                                {/* <label>Channel *</label> */}
                                                            </div>
                                                        </div>
                                                    </div>


                                                </div>
                                                <div className="inquiry-section-title mt-3">
                                                    Other Details
                                                </div>

                                                <div className="row g-3 d-flex align-items-end">

                                                    <div className="col-md-6 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                Inquiry Source
                                                            </label>
                                                            <div className="form-floating">
                                                                <Select
                                                                    options={sourceOptions}
                                                                    value={
                                                                        sourceOptions.find(
                                                                            (opt: any) => opt.value === formIds.source
                                                                        ) || null
                                                                    }
                                                                    onChange={(selected: any) => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            inquiry_source: selected?.label || "",
                                                                        }));

                                                                        setFormIds(prev => ({
                                                                            ...prev,
                                                                            source: selected?.value || null,
                                                                        }));
                                                                    }}
                                                                    placeholder="Inquiry Source"
                                                                    classNamePrefix="react-select"
                                                                    isDisabled={!isEditMode}
                                                                />

                                                            </div>
                                                            {/* <label>Inquiry Source</label> */}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-floating">
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                value={formData.inquiry_received_date}
                                                                onChange={(e) =>
                                                                    setFormData((prev: any) => ({
                                                                        ...prev,
                                                                        inquiry_received_date: e.target.value,
                                                                    }))
                                                                }
                                                                disabled={!isEditMode}
                                                            />

                                                            <label>Inquiry Received Date</label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                Responsible Person
                                                            </label>
                                                            <div className="form-floating">
                                                                <Select
                                                                    options={responsibleOptions}
                                                                    value={
                                                                        responsibleOptions.find(
                                                                            opt => opt.value === formIds.inquiry_owner
                                                                        ) || null
                                                                    }
                                                                    onChange={(selected: SelectOption | null) => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            responsible_person: selected?.label || "",
                                                                        }));

                                                                        setFormIds(prev => ({
                                                                            ...prev,
                                                                            inquiry_owner: selected?.value || null,
                                                                        }));
                                                                    }}
                                                                    placeholder="Responsible Person"
                                                                    classNamePrefix="react-select"
                                                                    isDisabled={!isEditMode}
                                                                />



                                                            </div>

                                                            {/* <label>Responsible Person</label> */}
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                Assign To
                                                            </label>
                                                            <Select
                                                                options={responsibleOptions}
                                                                value={
                                                                    responsibleOptions.find(
                                                                        opt => opt.value === formIds.inquiry_assigned_to
                                                                    ) || null
                                                                }
                                                                onChange={(selected: SelectOption | null) => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        assign_to: selected?.label || "",
                                                                    }));

                                                                    setFormIds(prev => ({
                                                                        ...prev,
                                                                        inquiry_assigned_to: selected?.value || null,
                                                                    }));
                                                                }}
                                                                placeholder="Assign To"
                                                                classNamePrefix="react-select"
                                                                isDisabled={!isEditMode}
                                                            />

                                                        </div>
                                                    </div>




                                                    <div className="col-md-12 stage-list">
                                                        <div className="form-group">
                                                            <label className="form-label fw-semibold my-1">
                                                                Probability
                                                            </label>
                                                            <div className="form-floating mb-2">
                                                                <Select
                                                                    options={probabilityOptions}
                                                                    value={
                                                                        probabilityOptions.find(
                                                                            (opt: any) => opt.value === formIds.probability
                                                                        ) || null
                                                                    }
                                                                    onChange={(selected: any) => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            probability: selected?.label || "",
                                                                        }));

                                                                        setFormIds(prev => ({
                                                                            ...prev,
                                                                            probability: selected?.value || null,
                                                                        }));
                                                                    }}



                                                                    placeholder="Probability"
                                                                    classNamePrefix="react-select"
                                                                    isDisabled={!isEditMode}

                                                                />
                                                            </div>
                                                            {/* <label>Probability</label> */}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-12">
                                                        <div className="form-floating">
                                                            <input
                                                                className="form-control"
                                                                value={formData.special_note}
                                                                onChange={(e) =>
                                                                    setFormData((prev: any) => ({
                                                                        ...prev,
                                                                        special_note: e.target.value,
                                                                    }))
                                                                }
                                                                disabled={!isEditMode}
                                                            />

                                                            <label>Special Note</label>
                                                        </div>
                                                    </div>


                                                    {isEditMode && (
                                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                                            <button
                                                                className="btn btn-primary px-4"
                                                                onClick={handleSaveInquiryOnly}
                                                            >
                                                                Save
                                                            </button>

                                                            <button
                                                                className="btn btn-outline-secondary px-4"
                                                                onClick={() => {
                                                                    fetchInquiryDetails(); // revert changes
                                                                    setIsEditMode(false);
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    )}

                                                </div>

                                            </div>
                                        </div>

                                        {/* ================= STATUS & TRACKING SECTION ================= */}
                                        <div className="col-lg-4">
                                            <div className="inquiry-section">

                                                <div className="inquiry-section-title">
                                                    Status & Tracking
                                                </div>



                                                <div className="stage-list mb-2">
                                                    <label className="form-label fw-semibold">
                                                        Stage <span className="text-danger">*</span>
                                                    </label>

                                                    <Select
                                                        options={stageOptions}
                                                        value={stage}
                                                        isDisabled={isWon}
                                                        onChange={(selected) => {
                                                            setPreviousStage(stage);   // store old stage
                                                            setStage(selected);
                                                        }}
                                                        placeholder="Select Stage"
                                                        classNamePrefix="react-select"
                                                        isOptionDisabled={isStageOptionDisabled}
                                                        components={{ Option: StageOptionComponent }}   // ⭐ ADD THIS
                                                    />



                                                </div>


                                                {rules.commentRequired && (
                                                    <div className="form-floating mb-2">
                                                        <textarea
                                                            className="form-control"
                                                            value={activityData.comment}
                                                            onChange={(e) => {
                                                                setIsActivityDirty(true);
                                                                setActivityData(prev => ({
                                                                    ...prev,
                                                                    comment: e.target.value,
                                                                }));
                                                            }}

                                                            required={rules.commentRequired}
                                                            style={{ height: "90px" }}
                                                        />

                                                        <label>
                                                            Comment {rules.commentRequired && <span className="text-danger">*</span>}
                                                        </label>
                                                    </div>
                                                )}


                                                {rules.date && (
                                                    <div className="form-floating mb-2">
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={activityData.date}
                                                            // onKeyDown={(e) => e.preventDefault()}
                                                            onChange={e => {
                                                                setActivityData(prev => ({
                                                                    ...prev,
                                                                    date: e.target.value,
                                                                }));
                                                            }}
                                                            // min={yesterday}   // ✅ allow yesterday
                                                            required={rules.date.required}
                                                        />

                                                        <label>
                                                            {rules.date.label || "Select Date"}
                                                            {rules.date.required && <span className="text-danger"> *</span>}
                                                        </label>
                                                    </div>
                                                )}




                                                {rules.assignedTo && (
                                                    <div className="stage-list mb-2">
                                                        <label className="form-label fw-semibold">
                                                            Assign To <span className="text-danger">*</span>
                                                        </label>

                                                        <Select
                                                            options={responsibleOptions}
                                                            value={activityData.assignedTo}
                                                            onChange={(selected: any) =>
                                                                setActivityData(prev => ({
                                                                    ...prev,
                                                                    assignedTo: selected,
                                                                }))
                                                            }
                                                            placeholder="Assign To"
                                                            classNamePrefix="react-select"
                                                        />
                                                    </div>
                                                )}



                                                {rules.status && (
                                                    <div className="stage-list mb-3">
                                                        <label className="form-label fw-semibold">
                                                            Status <span className="text-danger">*</span>
                                                        </label>

                                                        <Select
                                                            options={statusOptions}
                                                            value={activityData.status}
                                                            onChange={(selected: any) =>
                                                                setActivityData(prev => ({
                                                                    ...prev,
                                                                    status: selected,
                                                                }))
                                                            }
                                                            placeholder="Select Status"
                                                            classNamePrefix="react-select"
                                                        />
                                                    </div>
                                                )}


                                                <hr />

                                                {rules.quote && (
                                                    <div className="stage-list mb-2">
                                                        <label className="form-label fw-semibold">
                                                            Quote <span className="text-danger">*</span>
                                                        </label>

                                                        <Select
                                                            options={quoteOptions}
                                                            value={activityData.quote}
                                                            onChange={(selected) =>
                                                                setActivityData(prev => ({
                                                                    ...prev,
                                                                    quote: selected,
                                                                }))
                                                            }
                                                            getOptionLabel={(o: QuoteOption) => o.name}
                                                            getOptionValue={(o: QuoteOption) => String(o.id)}
                                                            classNamePrefix="react-select"
                                                            placeholder="Select Quote"
                                                        />

                                                    </div>
                                                )}



                                                {rules.hatchwayValue && (
                                                    <div className="form-floating mb-2">
                                                        <input
                                                            className="form-control"
                                                            placeholder="Hatchway Value*"
                                                            value={activityData.hatchwayValue}
                                                            onChange={e =>
                                                                setActivityData(prev => ({
                                                                    ...prev,
                                                                    hatchwayValue: e.target.value.replace(/[^\d.]/g, ""),
                                                                }))
                                                            }
                                                        />
                                                        <label>Hatchway Value * (₹)</label>
                                                    </div>
                                                )}




                                                {rules.rfqValue && (
                                                    <div className="form-floating mb-2">
                                                        <input
                                                            className="form-control"
                                                            placeholder="RFQ Value *"
                                                            value={activityData.rfqValue}
                                                            onChange={e =>
                                                                setActivityData(prev => ({
                                                                    ...prev,
                                                                    rfqValue: e.target.value.replace(/[^\d.]/g, ""),
                                                                }))
                                                            }
                                                        />
                                                        <label>RFQ Value *</label>
                                                    </div>
                                                )}


                                                {/* ================= ACTIONS ================= */}
                                                <div className="col-12 d-flex justify-content-end gap-3 mt-4">
                                                    <button
                                                        className="btn btn-primary px-4"
                                                        onClick={handleSaveActivity}
                                                        disabled={
                                                            stageKey === "new" ||
                                                            !isActivityValid()
                                                        }
                                                    >
                                                        Save Activity
                                                    </button>

                                                    <button onClick={() => navigate(`/${vertical}/inquiries`)} className="btn btn-secondary px-4">Cancel</button>
                                                </div>



                                            </div>
                                        </div>



                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={`inquiry-card mt-4 ${isWon ? "locked-section" : ""}`}
                    title={isWon ? "This section is locked because the stage is Won" : ""}>
                    <div className="inquiry-card-header">
                        <i className="fas fa-clipboard-list"></i>
                        <span>Documents</span>
                    </div>

                    <div className="inquiry-card-body">
                        <div
                            className={`taiga-attachments ${isDragActive ? "drop-active" : ""}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >

                            {/* HEADER */}
                            <input
                                type="file"
                                hidden
                                ref={replaceInputRef}
                                onChange={handleReplaceFileChange}
                            />

                            <div className="taiga-attachments-header">
                                <span className="fw-semibold">
                                    {(inquiry?.documents_data?.length || 0) + uploadDocs.length} Attachments
                                </span>
                                {!isWon && (

                                    <label className="btn btn-taiga-add btn-primary">

                                        <i className="fas fa-plus"></i>


                                        <input
                                            type="file"
                                            multiple
                                            hidden
                                            onChange={handleUploadDocChange}
                                        />
                                    </label>
                                )}
                            </div>

                            {/* EXISTING DOCUMENTS */}
                            {inquiry?.documents_data?.map((doc: any) => {
                                const fileName = doc.document_url.split("/").pop();

                                return (
                                    <div className={`taiga-attachment-row ${replacingDocId === doc.id ? "replacing" : ""
                                        }`}
                                        key={doc.id}>
                                        <div className="taiga-file-info">
                                            <i className={`${getFileIcon(fileName)} me-2`} />

                                            <div>
                                                <div className="fw-semibold">
                                                    {fileName}
                                                    {replacingDocId === doc.id && (
                                                        <span className="ms-2 text-warning small">
                                                            (Replacing: {replacingFileName})
                                                        </span>
                                                    )}
                                                </div>

                                                <small className="text-muted">
                                                    Uploaded by {doc.uploaded_by_name}
                                                </small>
                                                <div className="small text-muted">
                                                    {new Date(doc.date_updated).toLocaleString()}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="taiga-desc-area">
                                            <span className="text-muted small">
                                                {doc.description || "No description"}
                                            </span>
                                        </div>

                                        {!isWon && (

                                            <div className="taiga-actions">
                                                <a
                                                    href={doc.document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-link btn-sm"
                                                    title="View"
                                                    style={{ color: "var(--primary-color)" }}
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </a>

                                                <button
                                                    className="btn btn-link btn-sm text-warning"
                                                    title={replacingDocId === doc.id ? "Replacing…" : "Replace"}
                                                    disabled={replacingDocId === doc.id}
                                                    onClick={() => handleReplaceClick(doc.id, fileName)}
                                                >
                                                    <i
                                                        className={`fas fa-repeat ${replacingDocId === doc.id ? "fa-spin" : ""
                                                            }`}
                                                    />
                                                </button>

                                                <button
                                                    className="btn btn-link btn-sm text-danger"
                                                    title="Delete"
                                                    onClick={() => handleDeleteDocument(doc.id, fileName)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>


                                            </div>
                                        )}
                                    </div>
                                );
                            })}


                            {/* NEWLY SELECTED DOCUMENTS (NOT UPLOADED YET) */}

                            {uploadDocs.map((doc, index) => (
                                <div className="taiga-attachment-row" key={`new-${index}`}>
                                    <div className="taiga-file-info">
                                        <i className="fas fa-paperclip me-2 text-primary"></i>
                                        <div>
                                            <div className="fw-semibold">{doc.file.name}</div>
                                            <small className="text-muted">
                                                {(doc.file.size / 1024).toFixed(1)} KB
                                            </small>
                                        </div>
                                    </div>

                                    <div className="taiga-desc-area">
                                        {doc.isEditing ? (
                                            <>
                                                <input
                                                    className="form-control form-control-sm"
                                                    placeholder="Type a short desc"
                                                    value={doc.description}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setUploadDocs(prev => {
                                                            const updated = [...prev];
                                                            updated[index].description = value;
                                                            return updated;
                                                        });
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            setUploadDocs(prev => {
                                                                const updated = [...prev];
                                                                updated[index].isEditing = false;
                                                                return updated;
                                                            });
                                                        }
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm text-success"
                                                    onClick={() => {
                                                        setUploadDocs(prev => {
                                                            const updated = [...prev];
                                                            updated[index].isEditing = false;
                                                            return updated;
                                                        });
                                                    }}
                                                >
                                                    <i className="fas fa-save"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <span className="text-muted small">
                                                {doc.description || "No description"}
                                            </span>
                                        )}
                                    </div>

                                    <div className="taiga-actions">
                                        {!doc.isEditing && (
                                            <button
                                                className="btn btn-link btn-sm"
                                                onClick={() => {
                                                    setUploadDocs(prev => {
                                                        const updated = [...prev];
                                                        updated[index].isEditing = true;
                                                        return updated;
                                                    });
                                                }}
                                            >
                                                <i className="fas fa-pen"></i>
                                            </button>
                                        )}

                                        <button
                                            className="btn btn-link btn-sm text-danger"
                                            onClick={() => handleRemovePendingDoc(index, doc.file.name)}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}


                            {/* ACTION */}
                            {uploadDocs.length > 0 && !isWon && (
                                <div className="d-flex justify-content-end mt-3">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleUploadDocuments}
                                        disabled={isUploadingDocs}
                                    >
                                        {isUploadingDocs ? "Uploading..." : "Upload Documents"}
                                    </button>
                                </div>
                            )}



                            {!isDragActive && uploadDocs.length === 0 && (
                                <div className="text-center text-muted small mb-2 mt-2">
                                    Drag & drop files here
                                </div>
                            )}

                            {isDragActive && (
                                <div className="text-center text-primary fw-semibold mb-2 mt-2">
                                    Drop files here
                                </div>
                            )}
                        </div>
                    </div>


                </div>

                {/* ================= PAYMENT TERMS ================= */}

                {isWon && paymentTermsData && (
                    <div className="inquiry-card mt-4">
                        <div className="inquiry-card-header d-flex justify-content-between align-items-center">
                            <div>
                                <i className="fas fa-money-check-alt"></i>
                                <span className="ms-2">Payment Terms</span>
                            </div>

                            <button style={{ backgroundColor: "#fff", color: "#000" }}
                                className="btn btn-sm btn-secondary"
                                onClick={handleEditPaymentTerms}
                            >
                                <i className="fas fa-pen"></i>
                            </button>
                        </div>

                        <div className="inquiry-card-body p-0">

                            <div className="p-3 border-bottom bg-light d-flex justify-content-between">
                                <span className="fw-semibold">Quotation Amount</span>
                                <span className="badge bg-success fs-6">
                                    ₹{formatCurrency(paymentTermsData.quotation_amount)}
                                </span>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-bordered table-hover mb-0">

                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: "80px" }}>#</th>
                                            <th>Payment Term</th>
                                            <th style={{ width: "140px" }}>Amount</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {paymentTermsData.payment_terms?.length ? (
                                            paymentTermsData.payment_terms.map((term: any, index: number) => (
                                                <tr key={term.term_id}>
                                                    <td>{index + 1}</td>
                                                    <td>{term.name}</td>
                                                    <td className="text-left fw-semibold">
                                                        ₹{formatCurrency(term.amount)}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={3} className="text-center py-3">
                                                    No payment terms found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>

                                </table>
                            </div>

                        </div>
                    </div>
                )}

                {/* ================= ACTIVITY LOG ================= */}
                <div className="inquiry-card mt-4">
                    <div className="inquiry-card-header">
                        <i className="fas fa-clipboard-list"></i>
                        <span>Activity Log</span>
                    </div>
                    <div className="inquiry-card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        {/* <th>#</th> */}
                                        <th>Stage</th>
                                        <th>Comment</th>
                                        <th>Scheduled Activity</th>
                                        <th>Created By</th>
                                        <th>Assigned To</th>
                                        <th>Value</th>
                                        <th>Created Date</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loadingLogs ? (
                                        <tr>
                                            <td colSpan={9} className="text-center py-3">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : activityLogs.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="text-center py-3">
                                                No activity logs found
                                            </td>
                                        </tr>
                                    ) : (
                                        activityLogs.map((log, index) => (
                                            <tr key={log.id}>
                                                {/* <td>{index + 1}</td> */}
                                                <td>{log.stage_name}</td>
                                                <td>{log.comment || "-"}</td>
                                                <td>{formatDate(log.scheduled_activity_date)}</td>
                                                <td>{log.created_by_name}</td>
                                                <td>{log.activity_assigned_to_name}</td>
                                                <td>{formatNumericAmount(log.value)}</td>
                                                <td>{formatDate(log.date_created)}</td>
                                                <td>
                                                    <span
                                                        className={`badge ${log.status === "in_progress"
                                                            ? "bg-warning text-dark"
                                                            : "bg-success"
                                                            }`}
                                                    >

                                                        {formatStatus(log.status)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>

            </div>
        </>
    );
};

export default InquiryEdit;
const StageOptionComponent = (props: any) => {
    const { data, isDisabled } = props;

    return (
        <div
            {...props.innerProps}
            style={{
                padding: "8px 12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: isDisabled ? "#9aa0a6" : "#000",
                cursor: isDisabled ? "not-allowed" : "pointer",
            }}
        >
            <span>{data.label}</span>

            {isDisabled && (
                <span style={{ fontSize: "12px", opacity: 0.7 }}>
                    🔒
                </span>
            )}
        </div>
    );
};